import { Box, SxProps, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingBrandStrong,
  colorLandingInk,
  colorLandingLine,
  colorLandingMuted,
  colorLandingSurface,
  colorLandingText,
  fontLandingDisplay,
  radiusLandingPanel,
  shadowLandingRaised,
} from "src/design/tokens";
import { DashboardStatsRow } from "./dashboard/DashboardStatsRow";
import { DashboardBookingsCard } from "./dashboard/DashboardBookingsCard";
import { QuietSlotsPanel } from "./dashboard/QuietSlotsPanel";
import { OffersEventsCard } from "./dashboard/OffersEventsCard";

interface DashboardMockProps {
  sx?: SxProps;
}

/**
 * Scaled-down rendition of the restaurant dashboard, used as the landing
 * page's product shot. Purely decorative: no live data, no interaction.
 */
export const DashboardMock = ({ sx }: DashboardMockProps) => {
  const { t } = useTranslation();

  return (
    <Box
      aria-hidden="true"
      sx={{
        backgroundColor: colorLandingSurface,
        border: `1px solid ${colorLandingLine}`,
        borderRadius: radiusLandingPanel,
        boxShadow: shadowLandingRaised,
        overflow: "hidden",
        fontSize: 12.5,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${colorLandingLine}`,
          fontWeight: 600,
          color: colorLandingText,
        }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
          {t("landingPage.ecosystem.dashboard.restaurantSelector")}
        </Typography>
        <Box component="span" sx={{ color: colorLandingMuted }}>
          ▾
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography
          sx={{
            color: colorLandingBrandStrong,
            fontWeight: 600,
            fontSize: 11,
            mb: 0.375,
          }}
        >
          {t("landingPage.ecosystem.dashboard.date")}
        </Typography>
        <Typography
          sx={{
            fontFamily: fontLandingDisplay,
            fontSize: 20,
            lineHeight: 1.1,
            color: colorLandingInk,
          }}
        >
          {t("landingPage.ecosystem.dashboard.greeting")}
        </Typography>
        <Typography sx={{ color: colorLandingMuted, fontSize: 12, mt: 0.5, mb: 1.75 }}>
          {t("landingPage.ecosystem.dashboard.greetingSub")}
        </Typography>
        <Typography
          sx={{ fontWeight: 700, color: colorLandingInk, fontSize: 13, mb: 1.25 }}
        >
          {t("landingPage.ecosystem.dashboard.generalStats")}
        </Typography>

        <DashboardStatsRow />
        <DashboardBookingsCard />
        <QuietSlotsPanel />
        <OffersEventsCard />
      </Box>
    </Box>
  );
};
